import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { SharedModule } from '../../../core/shared.module';
import { SelectionModel } from '@angular/cdk/collections';
import {
  ColumnDefinition,
  CurrentSort,
  IconTableEvent,
} from '../../../interfaces/table.interface';
import { AppService } from '../../../service/app.service';
import { Utente } from '../../../interfaces/user';
import { PaginationComponent } from '../pagination/pagination.component';
import { FiltroService } from '../../../service/filtro.service';
import { BehaviorSubject, Subscription, switchMap } from 'rxjs';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { LoaderService } from '../../../service/loader.service';
import { DateConversionService } from '../../utilities/dateconversion.service';
import { Attori } from '../../enum/attori.enum';
import { AnagraficaTimesheet } from '../../../interfaces/timesheet.interface';
import { ElencoRisorse } from '../../../interfaces/elenco-risorse.interface';
import { ElencoRisorseService } from '../../../service/elenco-risorse.service';
import { FiltroElencoRisorse } from '../../../interfaces/filtro-elenco-risorse.interface';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
  selector: 'app-tabella-elenco-risorse',
  standalone: true,
  templateUrl: './tabella-elenco-risorse.component.html',
  imports: [SharedModule, PaginationComponent, NgxSkeletonLoaderModule, MatTooltipModule],
})
export class TabellaElencoRisorseComponent {
  @Output() selectionItemEvent = new EventEmitter();
  @Output() iconItemEvent = new EventEmitter();

  @Input() hasSelection: boolean = false;
  @Input() selection!: SelectionModel<any>;
  @Input() anagraficaTimesheet: AnagraficaTimesheet | null = null;
  @Input() attore: number = 0;
  attori = Attori;
  pageSubscriptions: Subscription = new Subscription();
  currentSort: CurrentSort = {
    flagAsc: true,
    orderBy: '',
  };
  totalPages: number = 0;
  currentPage: number = 0;
  pageOffset: number = 10;
  filtro: FiltroElencoRisorse = {
    nomeResponsabile: null,
    dataApprovazione: null,
    pageNumber: 1,
    pageSize: 10,
    orderBy: '',
    flagAsc: true,
    flagExport: false,
  };
  form: any = {};
  componentLoaded: boolean = false;
  private readonly pageSubject = new BehaviorSubject<number>(0);
  @Input() editable: boolean = true;
  @Input() elencoCount: number = 0;

  private _dataSource!: ElencoRisorse[];
  @Input() set dataSource(value: ElencoRisorse[]) {
    if (value) {
      this._dataSource = value;
      this.componentLoaded = true;
    }
  }

  get dataSource() {
    return this._dataSource;
  }
  isCheckboxEnabled: boolean = false;

  constructor(
    private appService: AppService,
    private filtroService: FiltroService,
    private loaderService: LoaderService,
    private elencoRisorseService: ElencoRisorseService,
    private dateConversionService: DateConversionService,
    private cd: ChangeDetectorRef
  ) {}

  utente: Utente = this.appService.user;

  ngOnInit(): void {
    this.filtroService.ultimoFiltroElencoRisorse(this.attore).subscribe((filtro) => {
      this.form = filtro;
    });
    if (this.filtroService.ultimaPaginaInfo.pageOffset) {
      this.pageOffset = this.filtroService.ultimaPaginaInfo.pageOffset;
    }
    if (this.filtroService.ultimaPaginaInfo.currentPage) {
      this.currentPage = this.filtroService.ultimaPaginaInfo.currentPage;
    }
    this.totalPages = Math.ceil(this.elencoCount / this.pageOffset);
    this.isCheckboxEnabled = this.dataSource.some(
      (element) => element.dataApprovazione === null
    );
  }

  handleSearchEvent(event: any): void {
    this.form = event;
    this.getPage();
  }

  exportDataToExcel(): void {
    this.loaderService.show();
    this.filtro.flagExport = true;
    this.pageSubscriptions.add(
      this.elencoRisorseService
        .elencoRisorse(this.filtro)
        .subscribe({
          next: (result) => {
            if (result) {
              const blob = new Blob([result], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
              });
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = 'ElencoRisorse.xlsx';
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              window.URL.revokeObjectURL(url);
            }
          },
          complete: () => this.loaderService.hide(),
          error: () => this.loaderService.hide(),
        })
    );
  }

  isVisible(col: ColumnDefinition, element: any): boolean {
    if (col.isVisible) return col?.isVisible(element);
    else return true;
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.filter(
      (row) => row.dataApprovazione === null
    ).length;
    return numSelected === numRows;
  }

  masterSelectionAll() {
    this.isAllSelected()
      ? this.selection.clear()
      : this.dataSource.forEach((row) => {
          if (row.dataApprovazione === null)
          this.selection.select(row);
        });
    this.selectionItemEvent.emit(this.selection);
  }

  masterSelection(item: any) {
    this.selection.toggle(item);
    this.selectionItemEvent.emit(this.selection);
  }

  onIconClick(item: ElencoRisorse, columnDef: string) {
    const data: IconTableEvent = { item: item, columnDef: columnDef };
    this.iconItemEvent.emit(data);
  }

  getCaretClass(
    orderBy: string,
    flagAsc: boolean
  ):
    | 'bi-caret-up'
    | 'bi-caret-up-fill'
    | 'bi-caret-down'
    | 'bi-caret-down-fill'
    | undefined {
    if (
      orderBy !== this.currentSort.orderBy ||
      (orderBy === this.currentSort.orderBy &&
        flagAsc !== this.currentSort.flagAsc)
    ) {
      return flagAsc ? 'bi-caret-down' : 'bi-caret-up';
    }

    return flagAsc ? 'bi-caret-down-fill' : 'bi-caret-up-fill';
  }

  sort(orderBy: string) {
    if (orderBy === this.currentSort.orderBy) {
      this.currentSort.flagAsc = !this.currentSort.flagAsc;
    } else {
      this.currentSort = {
        orderBy,
        flagAsc: true,
      };
    }
    this.getPage();
  }

  getPage(page: number = 0): void {
    this.componentLoaded = false;
    this.currentPage = page;
    this.filtroService.setUltimaPaginaInfo({
      currentPage: this.currentPage,
      pageOffset: this.pageOffset,
    });
    const convertEmptyStringsToNull = (obj: any) => {
      Object.keys(obj).forEach((key) => {
        if (obj[key] === '' && key !== 'nomeDelegante') {
          obj[key] = null;
        }
      });
      return obj;
    };

    const formValues = convertEmptyStringsToNull(this.form);
    this.filtro = {
      risorsa: formValues.risorsa,
      nomeRisorsa: formValues.nomeRisorsa,
      nomeResponsabile: formValues.nomeResponsabile,
      descrizioneUnitaOrganizzativaPrimoLivello: formValues.descrizioneUnitaOrganizzativaPrimoLivello,
      descrizioneUnitaOrganizzativa: formValues.descrizioneUnitaOrganizzativa,
      modificato: formValues.modificato,
      approvato: formValues.approvato,
      nomeDelegante: formValues.nomeDelegante,
      nomeApprovatore: formValues.nomeApprovatore,
      dataApprovazione: formValues.dataApprovazione,
      flagExport: false,
      pageNumber: page + 1,
      pageSize: this.pageOffset,
      flagAsc: this.currentSort.flagAsc,
      orderBy: this.currentSort.orderBy,
    };
    this.filtroService.setUltimoFiltroElencoRisorse(
      this.filtro,
      this.attore
    );
    this.loaderService.show();
    this.pageSubscriptions.add(
      this.elencoRisorseService
        .elencoRisorseCount(this.filtro)
        .pipe(
          switchMap((count: number) => {
            this.elencoCount = count;
            this.totalPages = Math.ceil(this.elencoCount / this.pageOffset);
            return this.elencoRisorseService.elencoRisorse(
              this.filtro
            );
          })
        )
        .subscribe({
          next: (data: ElencoRisorse[]) => {
            this.dataSource = data;
            this.appService.timestamp = '';
            this.cd.detectChanges();
          },
          complete: () => this.loaderService.hide(),
          error: () => this.loaderService.hide(),
        })
    );
    this.pageSubject.next(page);
    this.componentLoaded = true;
  }

  changerEvent(value: number): void {
    this.pageOffset = value;
    this.totalPages = Math.ceil(this.elencoCount / this.pageOffset);
    this.currentPage = Math.min(this.currentPage, this.totalPages - 1);
    this.getPage(this.currentPage);
    this.pageSubject.next(this.currentPage);
  }

  ngOnDestroy(): void {
    this.pageSubscriptions.unsubscribe();
  }
}
